import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AdminPageShell, { AdminSurface } from "../../../components/Admin/AdminPageShell";
import { getApiUrl, getMediaUrl } from "../../../utils/site";

const categories = ["Gold", "Silver", "Gemstones", "Design Trends", "Care & Maintenance", "Events", "Other"];

const EditBlog = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState({
    title: "",
    content: "",
    category: "Other",
    image_url: "",
    author: "",
    excerpt: "",
    status: "published",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const response = await axios.get(getApiUrl(`/api/blogs/${id}`));
        setBlog({
          title: response.data.title || "",
          content: response.data.content || "",
          category: response.data.category || "Other",
          image_url: response.data.image_url || "",
          author: response.data.author || "",
          excerpt: response.data.excerpt || "",
          status: response.data.status || "published",
        });
      } catch (error) {
        toast.error("Failed to load blog.");
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [id]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setBlog((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      await axios.put(
        getApiUrl(`/api/blogs/${id}`),
        {
          ...blog,
          author: blog.author || storedUser.name || storedUser.email || String(storedUser.id || "Admin"),
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      toast.success("Blog updated.");
      navigate("/admin/blogs");
    } catch (error) {
      toast.error(error.response?.data?.error || "Failed to update blog.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="roha-admin-empty">Loading blog...</div>;
  }

  return (
    <AdminPageShell
      eyebrow="Editorial management"
      title="Edit Blog"
      description="Update article content, image, excerpt, and publishing status from the unified admin workspace."
    >
      <AdminSurface title="Blog editor" subtitle={`Editing blog #${id}`}>
        <form className="roha-admin-form-grid" onSubmit={handleSubmit}>
          <div className="roha-admin-form-grid roha-admin-form-grid--two">
            <div className="roha-admin-field">
              <label>Title</label>
              <input className="form-control" name="title" value={blog.title} onChange={handleChange} required />
            </div>
            <div className="roha-admin-field">
              <label>Category</label>
              <select className="form-select" name="category" value={blog.category} onChange={handleChange}>
                {categories.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="roha-admin-form-grid roha-admin-form-grid--two">
            <div className="roha-admin-field">
              <label>Excerpt</label>
              <input className="form-control" name="excerpt" value={blog.excerpt} onChange={handleChange} />
            </div>
            <div className="roha-admin-field">
              <label>Status</label>
              <select className="form-select" name="status" value={blog.status} onChange={handleChange}>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
            </div>
          </div>

          <div className="roha-admin-field">
            <label>Image URL</label>
            <input className="form-control" name="image_url" value={blog.image_url} onChange={handleChange} />
          </div>

          {blog.image_url ? (
            <img src={getMediaUrl(blog.image_url)} alt="Blog preview" className="roha-admin-image-thumb" style={{ width: 160, height: 120 }} />
          ) : null}

          <div className="roha-admin-field">
            <label>Content</label>
            <textarea className="form-control" name="content" rows="9" value={blog.content} onChange={handleChange} required />
          </div>

          <div className="roha-admin-toolbar">
            <button type="submit" className="roha-admin-navbar__chip roha-admin-navbar__chip--primary" disabled={saving}>
              {saving ? "Saving..." : "Update blog"}
            </button>
            <button type="button" className="roha-admin-navbar__chip" onClick={() => navigate("/admin/blogs")}>
              Cancel
            </button>
          </div>
        </form>
      </AdminSurface>
    </AdminPageShell>
  );
};

export default EditBlog;
